import { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Dialog, DialogContent, Stack, Typography, useTheme } from '@mui/material'
import Otp from '../Otp'
import { PhoneInput, INDIA_COUNTRY_CODE, formatIndiaMobile, isValidIndiaMobileNumber } from './PhoneInput'
import { addMobileNumber, verifyMobileNumber } from '../../services/apis/user'
import { primary } from '../../assets/colors'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { setUserDetails } from '../../reducer/user.reducer'
import { selectUserDetails } from '../../selectors/user'
import { selectIsEmbedded } from '../../selectors/device'
import commonActions from '../../actions/common.actions'
import { updateDefaultScalperSettings } from '../../util/scalper'
import { setPinSetupToken } from '../../util/pinBrowserFlags'
import { parseErrorFromArrayBuffer } from '../common/errorParser'

const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = time % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

const MobileVerificationAlert = () => {
    const theme = useTheme()
    const userDetails = useSelector(selectUserDetails)
    const isEmbedded = useSelector(selectIsEmbedded)
    const dispatch = useDispatch()
    const user = userDetails?.currentUser
    const open = !isEmbedded && !!user && !user.mobileVerified

    const [mobileNumber, setMobileNumber] = useState('')
    const [otp, setOtp] = useState('')
    const [otpView, setOtpView] = useState(false)
    const [showInvalidOtpError, setShowInvalidOtpError] = useState(false)
    const [timer, setTimer] = useState(60)
    const [isTimerRunning, setIsTimerRunning] = useState(false)
    const [requestId, setRequestId] = useState('')
    const [disableActions, setDisableActions] = useState(false)

    const setUser = useCallback((updatedUser: Parameters<typeof updateDefaultScalperSettings>[0]) => {
        if (updatedUser) {
            dispatch(setUserDetails({
                ...userDetails,
                authenticated: true,
                currentUser: updatedUser
            }))
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'Mobile number verified successfully',
                errorType: 'success'
            })
        }
    }, [dispatch, userDetails])

    const handleVerify = useCallback(async () => {
        if (otpView) {
            AnalyticsManager.logEvent('Verify OTP Clicked', { otp })
            setDisableActions(true)
            try {
                const response = await verifyMobileNumber({
                    body: {
                        requestId,
                        verificationCode: otp
                    }
                })
                if (response.pinSetupToken) {
                    setPinSetupToken(response.pinSetupToken)
                }
                const updatedUser = await updateDefaultScalperSettings(response.user)
                setUser(updatedUser)
            } catch {
                setShowInvalidOtpError(true)
            }
            setDisableActions(false)
        } else {
            AnalyticsManager.logEvent('Verify Mobile Number Clicked', { mobile_number: formatIndiaMobile(mobileNumber) })
            setDisableActions(true)
            try {
                const response = await addMobileNumber({
                    body: {
                        countryCode: INDIA_COUNTRY_CODE,
                        mobileNumber
                    }
                })
                if (response.verificationRequired) {
                    setRequestId(response.requestId)
                    setOtpView(true)
                } else {
                    const updatedUser = await updateDefaultScalperSettings(response.user)
                    setUser(updatedUser)
                }
            } catch (error) {
                const { message } = parseErrorFromArrayBuffer(error)
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: message || 'Invalid mobile number',
                    errorType: 'error'
                })
            }
            setDisableActions(false)
        }
    }, [dispatch, mobileNumber, otp, otpView, requestId, setUser])

    const handleBackClick = useCallback(() => {
        AnalyticsManager.logEvent('Wrong Number Clicked')
        setOtpView(false)
        setOtp('')
        setShowInvalidOtpError(false)
    }, [])

    const handleResendOTP = useCallback(async () => {
        AnalyticsManager.logEvent('Resend OTP Clicked')
        if (!isValidIndiaMobileNumber(mobileNumber)) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: 'Please enter a valid mobile number',
                errorType: 'error'
            })
            return
        }
        try {
            // Re-send via add-mobile-number (pending number is not written to DB until verify).
            const response = await addMobileNumber({
                body: {
                    countryCode: INDIA_COUNTRY_CODE,
                    mobileNumber
                }
            })
            if (response.requestId) {
                setRequestId(response.requestId)
                setTimer(60)
                setIsTimerRunning(true)
            }
        } catch (error) {
            const { message } = parseErrorFromArrayBuffer(error)
            commonActions.setApiError(dispatch, {
                error: true,
                message: message || 'Failed to resend OTP',
                errorType: 'error'
            })
        }
    }, [dispatch, mobileNumber])

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | undefined
        if (isTimerRunning) {
            interval = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1)
            }, 1000)
        }
        return () => {
            if (interval) clearInterval(interval)
        }
    }, [isTimerRunning])

    useEffect(() => {
        if (timer === 0) {
            setIsTimerRunning(false)
        }
    }, [timer])

    useEffect(() => {
        if (otpView) {
            setTimer(60)
            setIsTimerRunning(true)
        }
    }, [otpView])

    useEffect(() => {
        if (showInvalidOtpError) {
            setShowInvalidOtpError(false)
        }
    }, [otp, showInvalidOtpError])

    useEffect(() => {
        if (otp.length === 6 && !disableActions) {
            handleVerify()
        }
    }, [otp])

    if (!open) {
        return null
    }

    const isPhoneValid = isValidIndiaMobileNumber(mobileNumber)

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            disableEscapeKeyDown
            onClose={() => {}}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    p: 1,
                    maxWidth: 480
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }
            }}
            sx={{
                zIndex: (theme) => theme.zIndex.modal + 1000
            }}
        >
            <DialogContent sx={{ px: 3, py: 3 }}>
                <Stack alignItems="center" spacing={1} width="100%">
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: theme.palette.text.secondary,
                            mb: 1
                        }}
                    >
                        {!otpView
                            ? 'Add your mobile number to continue'
                            : `Enter the verification code sent to ${formatIndiaMobile(mobileNumber)}`}
                    </Typography>
                    {otpView && (
                        <Stack alignItems="center" mb={1}>
                            <Button variant="text" onClick={handleBackClick}>
                                Wrong Number?
                            </Button>
                        </Stack>
                    )}
                    {otpView ? (
                        <Otp otp={otp} setOtp={setOtp} />
                    ) : (
                        <PhoneInput
                            value={mobileNumber}
                            onChange={setMobileNumber}
                        />
                    )}
                    {otpView && (
                        <Stack visibility={showInvalidOtpError ? 'visible' : 'hidden'} mt={2} alignItems="center">
                            <Typography variant="body2" fontWeight="bolder" color="error">
                                Invalid OTP Entered
                            </Typography>
                        </Stack>
                    )}
                    {otpView && (
                        <Stack py={2} justifyContent="space-around" alignItems="center">
                            <Typography variant="caption">Didn't receive an OTP?</Typography>
                            {timer > 0 ? (
                                <Typography variant="caption" className="font-feature-num">
                                    Resend OTP in: <span style={{ color: primary }}>{formatTime(timer)}</span>
                                </Typography>
                            ) : (
                                <Button sx={{ padding: '2px', fontSize: '12px' }} disabled={isTimerRunning} onClick={handleResendOTP}>
                                    Resend OTP
                                </Button>
                            )}
                        </Stack>
                    )}
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={disableActions || (otpView ? otp.length !== 6 || showInvalidOtpError : !isPhoneValid)}
                        onClick={handleVerify}
                        sx={{
                            borderRadius: '8px',
                            maxHeight: '48px',
                            mt: otpView ? 1 : 3,
                            maxWidth: '408px'
                        }}
                    >
                        Verify
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default memo(MobileVerificationAlert)
